import { api } from '../../api/client';
import { useLoader } from '../../api/loader';
import type { FormAbilityEntry, FormFields, IsoDate } from '../../api/types';
import type { AbilityKind, CharacterKind, Dorm, StudyClass } from '../../api/types';
import { t } from '../../i18n';
import type { ApiImage } from '../../shared/lib/media';

/**
 * Публичная карточка персонажа: типы ответа и его загрузка.
 *
 * Лежит отдельным файлом, потому что ответ читают ЧЕТЫРЕ экрана — само досье и
 * три его раздела (склад, питомцы, способности). Ручка одна и отдаёт всё сразу,
 * так что копии загрузки в каждом экране разъехались бы на первой же правке.
 */

/**
 * Ответ GET /api/registry/:characterId — публичная карточка.
 * Форма ответа описана здесь, а не в api/types.ts: сервер отдаёт принятую
 * версию анкеты одним объектом (она всегда есть, иначе персонажа в реестре
 * нет) и делит способности на действующие и истёкшие. Имена полей — как в
 * apps/api/src/modules/registry/registry.service.ts.
 */
/** Анкета в публичной карточке: те же поля минус идентификатор картинки —
 *  вместо него приходит готовый адрес. */
export interface CardForm extends Omit<FormFields, 'appearanceImageId' | 'abilities'> {
  abilities: FormAbilityEntry[];
  versionId: number;
  versionNo: number;
  appearanceImage: ApiImage | null;
}

export interface CardItem {
  id: number;
  title: string;
  description: string | null;
  image: ApiImage | null;
}

export interface CardPet {
  id: number;
  name: string;
  species: string | null;
  description: string | null;
  image: ApiImage | null;
  passportImage: ApiImage | null;
}

/**
 * Стартовая способность: девятый пункт анкеты, помеченный происхождением.
 * Строки в базе за ней нет, поэтому нет ни id, ни вида, ни ссылки на источник,
 * ни срока, ни картинки — и не «пока не заполнены», а вовсе. Действует, пока
 * опубликована версия анкеты.
 */
export interface CardFormAbilityRow extends FormAbilityEntry {
  origin: 'form';
}

/**
 * Выданная в игре: строка таблицы со сроком, подтверждением и, если её выдали
 * картинкой, изображением. Что это — способность или дебаф — говорит kind:
 * плоскость отдельная от происхождения, дебаф прилетает и вне квеста.
 */
export interface CardGrantedAbility {
  origin: 'granted';
  kind: AbilityKind;
  id: number;
  title: string;
  description: string | null;
  /** Ссылка на квест, пост или отыгрыш — то самое «откуда». */
  sourceLink: string | null;
  expiresAt: IsoDate | null;
  image: ApiImage | null;
}

/**
 * Строка общего списка: либо из анкеты, либо из таблицы. Третьего нет.
 * Объединение размеченное, а не тип с необязательными полями: обращение
 * к сроку действия стартовой способности должно быть ошибкой компиляции.
 */
export type CardAbility = CardFormAbilityRow | CardGrantedAbility;

export interface CharacterCardResponse {
  id: number;
  kind: CharacterKind;
  dorm: Dorm | null;
  studyClass: StudyClass | null;
  publishedAt: IsoDate | null;
  form: CardForm;
  items: CardItem[];
  pets: CardPet[];
  abilities: { active: CardAbility[]; archive: CardGrantedAbility[] };
  /** null, если игрок скрыл профиль или персонаж остался без автора. */
  player: { vkUserId: number; profileUrl: string } | null;
}

/**
 * Одна загрузка на все экраны досье. Ручка публичная и отдаёт карточку целиком,
 * поэтому разделу не нужен свой запрос — он берёт из ответа свою часть.
 */
export function useCharacterCard(characterId: number) {
  const valid = Number.isFinite(characterId) && characterId > 0;
  const state = useLoader(
    (signal) => api.get<CharacterCardResponse>(`/api/registry/${characterId}`, undefined, signal),
    [characterId],
    valid,
  );

  // Карточку отдаём только своего персонажа: иначе при сбое на переходе к
  // соседнему экран показал бы прежнего вместо ошибки. Номер NaN — это уходящая
  // панель, которая ещё рисуется с параметрами нового маршрута: её карточку
  // держим до конца анимации, а ошибку показываем, только если карточки не было.
  const card = valid ? (state.data?.id === characterId ? state.data : null) : state.data;
  const error = valid ? (state.error ?? '') : card === null ? t('registry.error.noCharacter') : '';

  return { card, error, loading: state.loading, retry: state.reload };
}

/** Имя для показа: русское, английское, обращение — что первым нашлось. */
export function displayName(form: CardForm): string {
  return form.nameRu ?? form.nameEn ?? form.nicknames ?? t('common.empty.noName');
}
